import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutomationError } from "~/api/errors";
import { useCollections, usePublishRun } from "~/api/queries";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";

type PublishMode = "create" | "append" | "replace";

const MODES: Array<{ value: PublishMode; label: string }> = [
  { value: "create", label: "Create a new document" },
  { value: "append", label: "Append to an existing document" },
  { value: "replace", label: "Replace an existing document" },
];

export interface PublishDialogProps {
  runId: string;
  open: boolean;
  onClose: () => void;
  /** Set when publishing one artifact rather than the run's own result. */
  artifactPath?: string | null;
}

/**
 * Publish a run's result — or one artifact — into a KnowIt document.
 *
 * kms seeds this form from the linked flow's `output` block. Slate cannot: `RunProjection.ToDto`
 * projects no `aiFlowSlug` (F-3), so there is no flow to read an output destination from and
 * every field starts empty. Guessing a collection would be worse than asking.
 */
export function PublishDialog({ runId, open, onClose, artifactPath }: PublishDialogProps) {
  const { t } = useTranslation();
  const publish = usePublishRun();
  const { data: collections } = useCollections();
  const [collectionId, setCollectionId] = useState("");
  const [parentDocumentId, setParentDocumentId] = useState("");
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<PublishMode>("create");
  const [problem, setProblem] = useState<string | null>(null);
  const [published, setPublished] = useState<{ documentId: string; title: string } | null>(null);

  useEffect(() => {
    if (open) {
      setProblem(null);
      setPublished(null);
    }
  }, [open]);

  const submit = () => {
    if (!collectionId) {
      setProblem(t("Pick a collection first."));
      return;
    }
    if (mode !== "create" && !parentDocumentId.trim()) {
      setProblem(
        t("Append and replace need a target document id — that IS the document being changed."),
      );
      return;
    }
    setProblem(null);
    publish.mutate(
      {
        id: runId,
        options: {
          ...(artifactPath ? { artifactPath } : {}),
          destination: {
            kind: "document",
            collectionId,
            ...(parentDocumentId.trim() ? { parentDocumentId: parentDocumentId.trim() } : {}),
            ...(title.trim() ? { titleTemplate: title.trim() } : {}),
            mode,
          },
        },
      },
      {
        onSuccess: (result) => setPublished({ documentId: result.documentId, title: result.title }),
        onError: (error: unknown) => {
          // `no_destination` / `bad_destination` arrive as codes, not sentences — keep the
          // server's own explanation rather than reconstructing one from the code.
          if (error instanceof AutomationError) {
            setProblem(error.message);
            return;
          }
          setProblem(error instanceof Error ? error.message : t("Publishing failed."));
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={artifactPath ? t("Save this artifact to KnowIt") : t("Save this result to KnowIt")}
      closeLabel={t("Close")}
      footer={
        published ? (
          <Button onClick={onClose}>{t("Done")}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button onClick={submit} disabled={publish.isPending}>
              {t("Save")}
            </Button>
          </>
        )
      }
    >
      {published ? (
        <p className="text-body text-text">
          {t("Saved as")}{" "}
          <Link
            to="/documents/$documentId"
            params={{ documentId: published.documentId }}
            className="text-accent hover:underline"
            onClick={onClose}
          >
            {published.title}
          </Link>
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {artifactPath ? (
            <p className="font-mono text-small text-text-secondary" dir="ltr">
              {artifactPath}
            </p>
          ) : null}

          <Select
            label={t("Collection")}
            value={collectionId}
            onChange={(event) => setCollectionId(event.target.value)}
          >
            <option value="">{t("Choose a collection…")}</option>
            {(collections ?? []).map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </Select>

          <Input
            label={t("Target document id")}
            value={parentDocumentId}
            onChange={(event) => setParentDocumentId(event.target.value)}
            placeholder={t("Optional for create")}
            hint={t(
              "For create: optional folder placement. For append or replace: this IS the document being changed.",
            )}
          />

          <Input
            label={t("Title")}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("Default: the flow's name and today's date")}
          />

          <fieldset className="flex flex-col gap-1.5">
            <legend className="text-small font-medium text-text-secondary">{t("Mode")}</legend>
            {MODES.map((entry) => (
              <label key={entry.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="publish-mode"
                  value={entry.value}
                  checked={mode === entry.value}
                  onChange={() => setMode(entry.value)}
                />
                <span className="text-body text-text">{t(entry.label)}</span>
              </label>
            ))}
          </fieldset>

          {problem ? (
            <p role="alert" className="text-small text-danger">
              {problem}
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}
